import React, { useState, useCallback, useMemo } from "react";
import {
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Backdrop,
  TextField,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Heading_Text_Color } from "../../constant/constant";

export default function DebitDataImport() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [file, setFile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageLoader, setPageLoader] = useState(false);
  const [dropdownValue, setDropdownValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const startsWithNumeric = (str) => /^\d/.test(str.trim());

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const parseFile = (file) => {
    setPageLoader(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim() !== "");
        const parsedData = lines.slice(1).map((line) => {
          const parts = line?.split(/\s{2,}/) ?? [];
          return {
            srNo: parts[0]?.trim(),
            trNo: parts[1]?.trim(),
            trDate: parts[2]?.trim(),
            trAmt: parts[3]?.trim(),
            trType: parts[4]?.trim(),
            station: parts[5]?.trim() || "",
            stationName: parts[6]?.trim() || "",
          };
        });

        const filtered = parsedData.filter(
          (row) =>
            row?.srNo &&
            typeof row.trType === "string" &&
            startsWithNumeric(row.srNo) &&
            row.trType.trim() !== ""
        );

        setData(filtered);
        setFilteredData(filtered);
        setFile(file);
        setCurrentPage(0);
        setSnackbar({
          open: true,
          message: `✅ Successfully parsed ${filtered.length} rows`,
          severity: "success",
        });
      } catch (error) {
        setSnackbar({ open: true, message: "❌ Failed to parse the file", severity: "error" });
      } finally {
        setPageLoader(false);
      }
    };

    reader.onerror = () => {
      setSnackbar({
        open: true,
        message: "❌ File reading error",
        severity: "error",
      });
      setPageLoader(false);
    };

    reader.readAsText(file);
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      if (file.type === "text/plain") parseFile(file);
      else {
        setSnackbar({
          open: true,
          message: "❌ Please upload a valid .txt file",
          severity: "warning",
        });
      }
    }
  }, [parseFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/plain": [".txt"] },
    multiple: false,
  });

  const handleChangePage = (event, newPage) => setCurrentPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const handleDropdownChange = (e) => setDropdownValue(e.target.value);

  const handleBottomButtonClick = () => {
    setSnackbar({
      open: true,
      message: `🚀 You selected "${dropdownValue}" with ${filteredData.length} rows`,
      severity: "info",
    });
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = data.filter((row) =>
      Object.values(row).some((val) => val?.toLowerCase().includes(value))
    );

    setFilteredData(filtered);
    setCurrentPage(0);
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    else if (sortConfig.key === key && sortConfig.direction === "descending") direction = null;

    setSortConfig({ key: direction ? key : null, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key] || "";
      const bVal = b[sortConfig.key] || "";
      if (sortConfig.direction === "ascending") return aVal.localeCompare(bVal);
      if (sortConfig.direction === "descending") return bVal.localeCompare(aVal);
      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = sortedData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const exportToCSV = () => {
    const headers = ["SR NO", "TR NO", "TR DATE", "TR AMT", "TR TYPE", "STATION", "STATIONNAME"];
    const rows = filteredData.map((row) =>
      [row.srNo, row.trNo, row.trDate, row.trAmt, row.trType, row.station, row.stationName].join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "debit_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⬍";
    return sortConfig.direction === "ascending" ? "🔼" : "🔽";
  };

  return (
    <div style={{ padding: 20 }}>
      <Backdrop open={pageLoader} style={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography gutterBottom style={{ color: Heading_Text_Color,  fontSize: '1.25rem'   }}>
        💾 Debit Data (RIB CRIS)
      </Typography>

      <Paper
        {...getRootProps()}
        style={{
          border: "2px dashed #1976d2",
          padding: 30,
          textAlign: "center",
          backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
          cursor: "pointer",
          marginBottom: 20,
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {isDragActive
            ? "Drop the file here ..."
            : "Drag & drop a `.txt` file here, or click to select"}
        </Typography>
      </Paper>

      {file && (
        <Typography variant="subtitle2" gutterBottom>
          📄 File: <strong>{file.name}</strong> | 📌 Total Records:{" "}
          <strong>{filteredData.length}</strong>
        </Typography>
      )}

      {filteredData.length > 0 && (
        <>
          <Grid container spacing={2} style={{ marginBottom: 10 }}>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearch}
              />
            </Grid>
            <Grid xs={12} sm={6} style={{ textAlign: "right" }}>
              <Button variant="contained" color="success" onClick={exportToCSV}>
                ⬇️ Export to CSV
              </Button>
            </Grid>
          </Grid>

          <div style={{ overflowX: "auto" }}>
            <table border="1" cellPadding="10" style={{ width: "100%", textAlign: "left" }}>
              <thead>
                <tr>
                  {[
                    ["srNo", "SR NO"],
                    ["trNo", "TR NO"],
                    ["trDate", "TR DATE"],
                    ["trAmt", "TR AMT"],
                    ["trType", "TR TYPE"],
                    ["station", "STATION"],
                    ["stationName", "STATIONNAME"],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => requestSort(key)}
                      style={{ cursor: "pointer" }}
                    >
                      {label} {renderSortIcon(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.srNo}</td>
                    <td>{row.trNo}</td>
                    <td>{row.trDate}</td>
                    <td>{row.trAmt}</td>
                    <td>{row.trType}</td>
                    <td>{row.station}</td>
                    <td>{row.stationName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination + Per Page + Info */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">Rows per page:</Typography>
              <FormControl size="small" style={{ marginLeft: 10, minWidth: 80 }}>
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  {[10, 25, 50, 100,500,1000].map((rows) => (
                    <MenuItem key={rows} value={rows}>
                      {rows}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <Typography variant="body2" style={{ flex: 1, textAlign: "center" }}>
              Showing {currentPage * rowsPerPage + 1}–
              {Math.min((currentPage + 1) * rowsPerPage, filteredData.length)} of{" "}
              {filteredData.length} records
            </Typography>

            <div>
              <Button
                disabled={currentPage === 0}
                onClick={() => handleChangePage(null, currentPage - 1)}
              >
                ⬅️ Previous
              </Button>
              <Button
                disabled={currentPage >= Math.ceil(filteredData.length / rowsPerPage) - 1}
                onClick={() => handleChangePage(null, currentPage + 1)}
              >
                Next ➡️
              </Button>
            </div>
          </div>

          {/* Bottom Action */}
          <Grid container spacing={2} style={{ marginTop: 20, alignItems: "center" }}>
            <Grid xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Select Option</InputLabel>
                <Select value={dropdownValue} onChange={handleDropdownChange} label="Select Option">
                  <MenuItem value="Submit to Server">Submit to Server</MenuItem>
                  <MenuItem value="Review Records">Review Records</MenuItem>
                  <MenuItem value="Validate and Export">Validate and Export</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleBottomButtonClick}
              >
                🚀 Take Action
              </Button>
            </Grid>
          </Grid>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
